import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Download, Mail, MessageSquare, FileText } from "lucide-react";
import { api, API } from "../lib/api";

/**
 * RentalDocumentModal — full-screen deep view of a rental, styled like an
 * Enterprise/Budget rental agreement. Opened by clicking a rental row/code.
 * Includes complete renter + DL details, vehicle, charges, condition report,
 * policies, T&Cs, PDF download and email/SMS delivery of the agreement.
 */

const fmtDt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function Overline({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] border-b border-white/10 pb-1.5 mb-3">{children}</div>;
}

function Field({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">{label}</div>
      <div className="text-sm text-[#F5F2EB] mt-0.5">{value || "—"}</div>
    </div>
  );
}

const statusPill = {
  reserved: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30",
  active:   "bg-emerald-400/10 text-emerald-400 border-emerald-400/30",
  returned: "bg-white/5 text-[#71717A] border-white/10",
  cancelled:"bg-red-400/10 text-red-400 border-red-400/30",
};

export default function RentalDocumentModal({ rental: initial, onClose, onChange }) {
  const [rental, setRental] = useState(initial);
  const [vehicle, setVehicle] = useState(null);
  const [terms, setTerms] = useState([]);
  const [companySig, setCompanySig] = useState(null);
  const [dlFront, setDlFront] = useState(null);
  const [dlBack, setDlBack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);   // "email" | "sms" | null
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [changed, setChanged] = useState(false);

  const rid = initial.rental_id;

  const reload = () =>
    api.get(`/admin/rental/rentals/${rid}`).then(r => setRental(r.data)).catch(() => {});

  useEffect(() => {
    reload();
    api.get("/admin/rental/settings").then(r => {
      setTerms(r.data.effective_terms || []);
      setCompanySig(r.data.company_signature || null);
    }).catch(() => setTerms([]));
    api.get(`/admin/rental/rentals/${rid}/dl-photo/front`).then(r => setDlFront(r.data.data_url)).catch(() => {});
    api.get(`/admin/rental/rentals/${rid}/dl-photo/back`).then(r => setDlBack(r.data.data_url)).catch(() => {});
    // eslint-disable-next-line
  }, [rid]);

  useEffect(() => {
    if (!vehicle && rental?.vehicle_id) {
      api.get("/admin/rental/fleet")
        .then(r => setVehicle((r.data || []).find(v => v.rental_vehicle_id === rental.vehicle_id) || null))
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [rental?.vehicle_id]);

  const close = () => { if (changed) onChange(); else onClose(); };

  const afterAction = async () => { setChanged(true); await reload(); };

  const downloadPdf = () => window.open(`${API}/admin/rental/rentals/${rid}/agreement.pdf`, "_blank");

  const copySignLink = async () => {
    try {
      const r = await api.get(`/admin/rental/rentals/${rid}/sign-link`);
      try { await navigator.clipboard.writeText(r.data.sign_url); } catch {/* ignore */}
      toast.success("E-sign link copied — send it to the renter");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not get sign link");
    }
  };

  const sendAgreement = async (channel) => {
    setSending(channel);
    try {
      const r = await api.post(`/admin/rental/rentals/${rid}/send-agreement`, {
        via_email: channel === "email",
        via_sms: channel === "sms",
      });
      const status = channel === "email" ? r.data.email : r.data.sms;
      if (status === "sent") toast.success(channel === "email" ? "Agreement PDF emailed to renter" : "Document link texted to renter");
      else if (status === "MOCKED") toast.info("Notification service in mock mode — logged but not delivered");
      else if (String(status).startsWith("skipped")) toast.error(channel === "email" ? "Renter has no email on file" : "Renter has no phone on file");
      else toast.error(`Delivery failed (${status})`);
      await afterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(null); }
  };

  const sendSquareLink = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/rental/rentals/${rid}/square-checkout`);
      try { await navigator.clipboard.writeText(r.data.payment_url); } catch {/* ignore */}
      toast.success("Square link copied to clipboard");
      await afterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Square link failed");
    } finally { setBusy(false); }
  };

  const markPaid = async () => {
    if (!window.confirm("Mark this rental as paid (off-Square / cash / check)?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/rental/rentals/${rid}/mark-paid`, { method: "cash", note: "Manual mark-paid" });
      toast.success("Marked paid");
      await afterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this rental?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/rental/rentals/${rid}/cancel`);
      toast.success("Rental cancelled");
      await afterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const pricing = rental.pricing || {};
  const paid = rental.payment_status === "paid";
  const total = rental.grand_total || rental.total || 0;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto" data-testid="rental-document-modal">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <FileText size={16} className="text-[#D4AF37] flex-shrink-0"/>
          <span className="font-mono text-[#D4AF37] text-lg" data-testid="rental-doc-code">{rental.rental_code}</span>
          <span className={`text-[9px] uppercase tracking-[0.22em] px-2 py-1 border ${statusPill[rental.status] || statusPill.reserved}`}>{rental.status}</span>
          {rental.source === "turo" && (
            <span data-testid="rental-doc-turo-badge"
              className="text-[9px] font-bold uppercase tracking-[0.22em] px-2 py-1 border border-amber-500/50 text-amber-600 bg-amber-400/10">
              Turo
            </span>
          )}
          {paid && <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-1 border border-emerald-400/40 text-emerald-400">Paid</span>}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="rental-doc-download-pdf" onClick={downloadPdf}
            className="text-[10px] uppercase tracking-[0.18em] px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/5 inline-flex items-center gap-1.5">
            <Download size={12}/> PDF
          </button>
          <button data-testid="rental-doc-email" onClick={() => sendAgreement("email")} disabled={sending !== null}
            className="text-[10px] uppercase tracking-[0.18em] px-3 py-2 border border-white/15 text-[#F5F2EB] hover:border-white/40 inline-flex items-center gap-1.5 disabled:opacity-50">
            <Mail size={12}/> {sending === "email" ? "Sending…" : "Email PDF"}
          </button>
          <button data-testid="rental-doc-sms" onClick={() => sendAgreement("sms")} disabled={sending !== null}
            className="text-[10px] uppercase tracking-[0.18em] px-3 py-2 border border-white/15 text-[#F5F2EB] hover:border-white/40 inline-flex items-center gap-1.5 disabled:opacity-50">
            <MessageSquare size={12}/> {sending === "sms" ? "Sending…" : "Text link"}
          </button>
          <button data-testid="rental-doc-close" onClick={close} className="text-[#A1A1AA] hover:text-white p-2"><X size={18}/></button>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-4xl mx-auto my-6 md:my-10 bg-[#0A0A0A] border border-white/10">
        {/* Doc header */}
        <div className="px-6 md:px-10 py-8 border-b-2 border-[#D4AF37]/60">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <div className="font-serif text-2xl text-[#F5F2EB]">Delux Limo Trans Group</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] mt-1">
                deluxlimotransgroup.com · (414) 333-6275 · reservations@deluxlimotransgroup.com
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-[#F5F2EB] uppercase tracking-[0.14em]">Car Rental Agreement</div>
              <div className="text-[11px] text-[#71717A] mt-1">Agreement / Invoice #{rental.rental_code}</div>
              <div className="text-[11px] text-[#71717A]">Booked {fmtDt(rental.created_at)}</div>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-10 py-8 space-y-8">
          {/* Renter */}
          <section data-testid="rental-doc-renter">
            <Overline>Renter / Driver Details</Overline>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
              <Field label="Full name" value={rental.renter_name} testid="doc-renter-name"/>
              <Field label="Phone" value={rental.renter_phone}/>
              <Field label="Email" value={rental.renter_email}/>
              <Field label="Address" value={rental.renter_address}/>
              <Field label="Driver's license #" value={rental.renter_dl_number ? `${rental.renter_dl_number}${rental.renter_dl_state ? ` (${rental.renter_dl_state})` : ""}` : "—"} testid="doc-renter-dl"/>
              <Field label="DL expiry" value={rental.renter_dl_expiry}/>
              <Field label="Date of birth" value={rental.renter_dob}/>
              <Field label="Personal insurance" value={rental.insurance_provider}/>
              <Field label="Policy #" value={rental.insurance_policy}/>
              <Field label="Additional drivers" value={String(rental.additional_drivers || 0)}/>
            </div>
            {(dlFront || dlBack) && (
              <div className="mt-5">
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A] mb-2">Driver&apos;s license photos</div>
                <div className="flex flex-wrap gap-4">
                  {dlFront && (
                    <a href={dlFront} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={dlFront} alt="DL front" className="h-32 border border-white/10 hover:border-[#D4AF37]/40" data-testid="rental-doc-dl-front"/>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A] mt-1">Front</div>
                    </a>
                  )}
                  {dlBack && (
                    <a href={dlBack} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={dlBack} alt="DL back" className="h-32 border border-white/10 hover:border-[#D4AF37]/40" data-testid="rental-doc-dl-back"/>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A] mt-1">Back</div>
                    </a>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Vehicle */}
          <section data-testid="rental-doc-vehicle">
            <Overline>Vehicle</Overline>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
              <Field label="Vehicle" value={rental.vehicle_name} testid="doc-vehicle-name"/>
              <Field label="License plate" value={rental.vehicle_plate}/>
              <Field label="VIN" value={vehicle?.vin}/>
              <Field label="Color" value={vehicle?.color}/>
              <Field label="Body / seats" value={vehicle ? `${vehicle.body_type} · ${vehicle.seats} seats` : "—"}/>
              <Field label="Transmission / fuel" value={vehicle ? `${vehicle.transmission} · ${vehicle.fuel_type}` : "—"}/>
            </div>
          </section>

          {/* Rental period */}
          <section data-testid="rental-doc-period">
            <Overline>Rental Period</Overline>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
              <Field label="Pickup" value={fmtDt(rental.pickup_datetime)}/>
              <Field label="Return" value={fmtDt(rental.return_datetime)}/>
              <Field label="Duration" value={`${pricing.days || 1} day${(pricing.days || 1) === 1 ? "" : "s"} · ${(pricing.rate_type || "daily")} rate`}/>
              <Field label="Pickup location" value={rental.pickup_location}/>
              <Field label="Return location" value={rental.return_location}/>
              {rental.series_code && <Field label="Recurring series" value={`${rental.series_code} · ${rental.series_index}/${rental.series_total}`}/>}
            </div>
          </section>

          {/* Charges */}
          <section data-testid="rental-doc-charges">
            <div className="flex justify-between items-baseline border-b border-white/10 pb-1.5 mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Charges &amp; Fees</div>
              {paid && <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-400">PAID · {rental.payment_method || "card"}</span>}
              {rental.payment_status === "link_sent" && <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Square link sent</span>}
            </div>
            <div className="text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-[#71717A]">{pricing.days} × {money(pricing.rate)} ({(pricing.rate_type || "daily")} rate)</span><span className="text-[#F5F2EB]">{money(pricing.base)}</span></div>
              {pricing.additional_driver_fee_total > 0 && <div className="flex justify-between"><span className="text-[#71717A]">Additional drivers ({rental.additional_drivers})</span><span className="text-[#F5F2EB]">{money(pricing.additional_driver_fee_total)}</span></div>}
              {pricing.additional_insurance_fee_total > 0 && <div className="flex justify-between"><span className="text-[#71717A]">Coverage · {rental.insurance_option_label || "Damage & liability"}</span><span className="text-[#F5F2EB]">{money(pricing.additional_insurance_fee_total)}</span></div>}
              {pricing.discount > 0 && <div className="flex justify-between"><span className="text-[#71717A]">Discount</span><span className="text-red-400">−{money(pricing.discount)}</span></div>}
              <div className="flex justify-between"><span className="text-[#71717A]">Subtotal</span><span className="text-[#F5F2EB]">{money(pricing.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#71717A]">Tax (5.5%)</span><span className="text-[#F5F2EB]">{money(pricing.tax)}</span></div>
              {rental.overage_charge > 0 && <div className="flex justify-between"><span className="text-[#71717A]">Mileage overage ({rental.overage_miles} mi × {money(pricing.extra_mile_rate)})</span><span className="text-red-400">+{money(rental.overage_charge)}</span></div>}
              {rental.damage_charge > 0 && <div className="flex justify-between"><span className="text-[#71717A]">Damage charge</span><span className="text-red-400">+{money(rental.damage_charge)}</span></div>}
              {rental.late_fee > 0 && <div className="flex justify-between"><span className="text-[#71717A]">Late return fee</span><span className="text-red-400">+{money(rental.late_fee)}</span></div>}
              {rental.fuel_charge > 0 && <div className="flex justify-between"><span className="text-[#71717A]">Fuel charge</span><span className="text-red-400">+{money(rental.fuel_charge)}</span></div>}
              <div className="flex justify-between pt-2 border-t border-white/10 text-[#D4AF37] font-bold text-lg"><span>TOTAL</span><span data-testid="rental-doc-total">{money(total)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-[#71717A]">Security deposit (refundable)</span><span className="text-[#F5F2EB]">{money(pricing.security_deposit)}</span></div>
              <div className="flex justify-between text-xs font-bold"><span className="text-[#71717A]">Balance due</span><span className={paid ? "text-emerald-400" : "text-red-400"}>{paid ? "$0.00" : money(total)}</span></div>
            </div>
            {rental.square_payment_url && !paid && (
              <div className="mt-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A] mb-1">Payment link (Square)</div>
                <a href={rental.square_payment_url} target="_blank" rel="noopener noreferrer"
                   data-testid="rental-doc-pay-link"
                   className="text-[#D4AF37] underline break-all text-xs">{rental.square_payment_url}</a>
              </div>
            )}
          </section>

          {/* Condition report */}
          {(rental.checked_out_at || rental.returned_at) && (
            <section data-testid="rental-doc-condition">
              <Overline>Vehicle Condition Report</Overline>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
                <Field label="Checkout odometer" value={rental.pickup_odometer != null ? `${rental.pickup_odometer} mi` : "—"}/>
                <Field label="Checkout fuel" value={rental.fuel_level_pickup}/>
                <Field label="Return odometer" value={rental.return_odometer != null ? `${rental.return_odometer} mi` : "—"}/>
                <Field label="Return fuel" value={rental.fuel_level_return}/>
                {rental.miles_driven != null && <Field label="Miles driven" value={`${rental.miles_driven} mi`}/>}
                {rental.overage_miles != null && <Field label="Overage miles" value={`${rental.overage_miles} mi`}/>}
              </div>
              {(rental.pickup_notes || rental.return_notes) && (
                <div className="mt-3 text-xs text-[#71717A] space-y-1">
                  {rental.pickup_notes && <div><span className="uppercase tracking-[0.18em] text-[9px]">Checkout notes:</span> <span className="text-[#F5F2EB]">{rental.pickup_notes}</span></div>}
                  {rental.return_notes && <div><span className="uppercase tracking-[0.18em] text-[9px]">Return notes:</span> <span className="text-[#F5F2EB]">{rental.return_notes}</span></div>}
                </div>
              )}
            </section>
          )}

          {/* Policy summary */}
          <section data-testid="rental-doc-policy">
            <Overline>Mileage &amp; Fuel Policy</Overline>
            <p className="text-xs text-[#A1A1AA] leading-relaxed">
              Included mileage: <span className="text-[#F5F2EB]">{pricing.mileage_included_total || "—"} miles total</span>
              {vehicle ? ` (${vehicle.mileage_included_per_day} mi/day × ${pricing.days || 1} days)` : ""}. Overage billed at{" "}
              <span className="text-[#F5F2EB]">{money(pricing.extra_mile_rate || 0.35)}/mile</span>. The vehicle must be returned
              with the fuel level recorded at checkout.
            </p>
          </section>

          {/* Notes */}
          {rental.notes && (
            <section>
              <Overline>Notes</Overline>
              <p className="text-xs text-[#F5F2EB]">{rental.notes}</p>
            </section>
          )}

          {/* Terms & conditions */}
          <section data-testid="rental-doc-terms">
            <Overline>Terms &amp; Conditions</Overline>
            <div className="space-y-3">
              {terms.map((t, i) => (
                <div key={i}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#F5F2EB]">{t.title}</div>
                  <p className="text-[11px] text-[#71717A] leading-relaxed whitespace-pre-line mt-0.5">{t.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Signatures */}
          <section className="pt-4" data-testid="rental-doc-signatures">
            <div className="flex justify-between items-baseline border-b border-white/10 pb-1.5 mb-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Signatures</div>
              {rental.renter_signature ? (
                <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-400" data-testid="rental-doc-signed-badge">
                  E-signed {fmtDt(rental.renter_signed_at)}
                </span>
              ) : (
                <button data-testid="rental-doc-copy-sign-link" onClick={copySignLink}
                  className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline">
                  Copy e-sign link
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                {rental.renter_signature ? (
                  <img src={`data:image/png;base64,${rental.renter_signature}`} alt="Renter signature"
                       className="h-16 bg-white px-3 py-1 border-b border-[#F5F2EB]/60" data-testid="rental-doc-renter-sig"/>
                ) : (
                  <div className="border-b border-[#F5F2EB]/60 h-10 flex items-end pb-1">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] italic">Awaiting e-signature</span>
                  </div>
                )}
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A] mt-1.5">Renter signature — {rental.renter_name}</div>
                <div className="text-[9px] text-[#71717A] mt-0.5">
                  {rental.renter_signature ? `Signed electronically ${fmtDt(rental.renter_signed_at)}` : "Date: ____________________"}
                </div>
              </div>
              <div>
                {companySig ? (
                  <img src={`data:image/png;base64,${companySig}`} alt="Company signature"
                       className="h-16 bg-white px-3 py-1 border-b border-[#F5F2EB]/60" data-testid="rental-doc-company-sig"/>
                ) : (
                  <div className="border-b border-[#F5F2EB]/60 h-10"/>
                )}
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A] mt-1.5">Delux Limo Trans Group — authorized representative</div>
                <div className="text-[9px] text-[#71717A] mt-0.5">Date: {companySig ? fmtDt(rental.created_at) : "____________________"}</div>
              </div>
            </div>
          </section>

          {/* Timeline meta */}
          <section className="text-[10px] text-[#71717A] border-t border-white/5 pt-4 space-y-0.5" data-testid="rental-doc-timeline">
            <div>Created {fmtDt(rental.created_at)}</div>
            {rental.square_link_created_at && <div>Payment link generated {fmtDt(rental.square_link_created_at)}</div>}
            {rental.agreement_sent_at && <div>Agreement sent {fmtDt(rental.agreement_sent_at)}</div>}
            {rental.renter_signed_at && <div>E-signed by renter {fmtDt(rental.renter_signed_at)}</div>}
            {rental.checked_out_at && <div>Checked out {fmtDt(rental.checked_out_at)}</div>}
            {rental.returned_at && <div>Returned {fmtDt(rental.returned_at)}</div>}
            {rental.paid_at && <div>Paid {fmtDt(rental.paid_at)}</div>}
            {rental.cancelled_at && <div>Cancelled {fmtDt(rental.cancelled_at)}</div>}
          </section>
        </div>

        {/* Bottom action bar */}
        <div className="flex flex-wrap justify-end gap-2 px-6 md:px-10 py-5 border-t border-white/10">
          {!paid && (
            <>
              <button data-testid="rental-square-link" onClick={sendSquareLink} disabled={busy}
                className="btn-ghost text-[11px] text-[#D4AF37] border-[#D4AF37]/40">
                {rental.square_payment_url ? "Regenerate Square link" : "Send Square payment link"}
              </button>
              <button data-testid="rental-mark-paid" onClick={markPaid} disabled={busy} className="btn-ghost text-[11px]">Mark paid</button>
            </>
          )}
          {rental.status === "reserved" && (
            <>
              <button data-testid="rental-checkout" onClick={() => setCheckoutOpen(true)} disabled={busy} className="btn-gold text-[11px]">Check out</button>
              <button data-testid="rental-cancel" onClick={cancel} disabled={busy} className="btn-ghost text-[11px] text-red-400">Cancel rental</button>
            </>
          )}
          {rental.status === "active" && (
            <button data-testid="rental-return" onClick={() => setReturnOpen(true)} disabled={busy} className="btn-gold text-[11px]">Return</button>
          )}
          <button data-testid="rental-doc-done" onClick={close} className="btn-ghost text-[11px]">Close</button>
        </div>
      </div>

      {checkoutOpen && <CheckoutOrReturnModal mode="checkout" rental={rental} onClose={() => setCheckoutOpen(false)} onDone={afterAction}/>}
      {returnOpen && <CheckoutOrReturnModal mode="return" rental={rental} onClose={() => setReturnOpen(false)} onDone={afterAction}/>}
    </div>
  );
}

export function CheckoutOrReturnModal({ mode, rental, onClose, onDone }) {
  const [f, setF] = useState({
    odometer: mode === "return" ? (rental.pickup_odometer || 0) + 100 : (rental.pickup_odometer || 0),
    fuel: "full", notes: "",
    damage_charge: 0, late_fee: 0, fuel_charge: 0,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "checkout") {
        await api.post(`/admin/rental/rentals/${rental.rental_id}/checkout`, {
          pickup_odometer: Number(f.odometer),
          fuel_level_pickup: f.fuel,
          pickup_notes: f.notes,
        });
        toast.success("Checked out · vehicle marked rented");
      } else {
        await api.post(`/admin/rental/rentals/${rental.rental_id}/return`, {
          return_odometer: Number(f.odometer),
          fuel_level_return: f.fuel,
          return_notes: f.notes,
          damage_charge: Number(f.damage_charge || 0),
          late_fee: Number(f.late_fee || 0),
          fuel_charge: Number(f.fuel_charge || 0),
        });
        toast.success("Returned · vehicle available again");
      }
      onClose();
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" data-testid={`${mode}-modal`}>
      <div className="bg-[#0A0A0A] border border-white/10 max-w-lg w-full">
        <div className="flex justify-between px-6 py-4 border-b border-white/5">
          <div className="font-serif text-lg text-[#F5F2EB]">{mode === "checkout" ? "Check out · pickup" : "Return · dropoff"}</div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>
        <div className="p-6 space-y-3 text-xs">
          <div><label className="field-label">Odometer reading</label><input type="number" className="field-input" data-testid="co-odometer" value={f.odometer} onChange={e => upd("odometer", e.target.value)}/></div>
          <div><label className="field-label">Fuel level</label>
            <select className="field-input" data-testid="co-fuel" value={f.fuel} onChange={e => upd("fuel", e.target.value)}>
              {["empty","1/4","1/2","3/4","full"].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          {mode === "return" && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="field-label">Damage charge ($)</label><input type="number" step="0.01" className="field-input" data-testid="co-damage" value={f.damage_charge} onChange={e => upd("damage_charge", e.target.value)}/></div>
                <div><label className="field-label">Late fee ($)</label><input type="number" step="0.01" className="field-input" data-testid="co-late" value={f.late_fee} onChange={e => upd("late_fee", e.target.value)}/></div>
                <div><label className="field-label">Fuel charge ($)</label><input type="number" step="0.01" className="field-input" data-testid="co-fuel-charge" value={f.fuel_charge} onChange={e => upd("fuel_charge", e.target.value)}/></div>
              </div>
              <div className="text-[10px] text-[#71717A]">Mileage overage is auto-calculated from odometer readings.</div>
            </>
          )}
          <div><label className="field-label">Notes</label><textarea rows={2} className="field-input" data-testid="co-notes" value={f.notes} onChange={e => upd("notes", e.target.value)}/></div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/5">
          <button onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
          <button data-testid="co-submit" onClick={submit} disabled={busy} className="btn-gold text-[11px]">
            {busy ? "Saving…" : (mode === "checkout" ? "Check out" : "Complete return")}
          </button>
        </div>
      </div>
    </div>
  );
}
